import { supabase, toError } from './supabase';
import { parsePreparedCheckoutAttemptId } from './checkoutLifecycleContract';
import type { SupportedCurrency } from '../utils/currency';

type CreateCheckoutSessionInput = {
    lookupKey: string;
    currency: SupportedCurrency;
    checkoutIntent?: 'starter_trial';
    checkoutAttemptId?: string;
    expectedDiscountGrantId?: string;
    previewProjectId?: string;
    resultDigestId?: string;
    replaceCheckoutAttemptId?: string;
    interactionMode?: CheckoutInteractionMode;
};

type CreateCheckoutSessionResponse = {
    url?: string;
    client_secret?: string;
    publishable_key?: string;
    checkout_attempt_id?: string;
    checkout_terms?: unknown;
    error?: string;
    code?: string;
    retryable?: boolean;
    clear_attempt?: boolean;
};

export type CheckoutTerms = {
    version: 'preview_trial_v1';
    planId: 'starter' | 'plus' | 'pro';
    lookupKey: string;
    currency: SupportedCurrency;
    interval: 'month' | 'year';
    regularRecurringAmountMinor: number;
    recurringAmountMinor: number;
    amountDueTodayMinor: number;
    percentOff: number | null;
    discountDuration: 'continuous_subscription' | null;
    trialPeriodDays: number;
    taxBehavior: 'exclusive';
};

export type EmbeddedCheckoutSession = {
    clientSecret: string;
    publishableKey: string | null;
    checkoutAttemptId: string | null;
    checkoutTerms: CheckoutTerms | null;
};

export type PreparedCheckoutSession = {
    checkoutAttemptId: string;
    checkoutTerms: CheckoutTerms;
};

export type CheckoutInteractionMode = 'prepare' | 'start';

const CHECKOUT_SESSION_MAX_ATTEMPTS_WITH_ID = 2;
const CHECKOUT_SESSION_RETRY_DELAY_MS = 750;
const RETRYABLE_FUNCTION_ERROR_NAMES = new Set(['FunctionsFetchError', 'FunctionsRelayError']);
export const ACCOUNT_OFFER_UNAVAILABLE_CODE = 'account_offer_unavailable';
export const PREVIEW_CHECKOUT_TERMS_STALE_CODE = 'preview_checkout_terms_stale';
export const CHECKOUT_SESSION_REPLACED_CODE = 'checkout_session_replaced';

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
    return Number.isInteger(value) && Number(value) >= 0;
}

function parseCheckoutTerms(value: unknown): CheckoutTerms | null {
    if (value === undefined || value === null) return null;
    if (!isRecord(value)) {
        throw new CheckoutSessionError('Checkout returned invalid trial terms', {
            code: 'invalid_checkout_terms',
        });
    }

    const percentOff = value.percent_off;
    const validPercentOff = percentOff === null || (
        typeof percentOff === 'number'
        && Number.isFinite(percentOff)
        && percentOff > 0
        && percentOff <= 100
    );
    const discountDuration = value.discount_duration;
    const validDiscountDuration = percentOff === null
        ? discountDuration === null
        : discountDuration === 'continuous_subscription';
    const currency = typeof value.currency === 'string'
        ? value.currency.toUpperCase()
        : '';
    const regularAmount = value.regular_recurring_amount_minor;
    const recurringAmount = value.recurring_amount_minor;

    if (
        value.version !== 'preview_trial_v1'
        || (value.plan_id !== 'starter' && value.plan_id !== 'plus' && value.plan_id !== 'pro')
        || typeof value.lookup_key !== 'string'
        || value.lookup_key.length === 0
        || !['USD', 'EUR', 'GBP', 'CNY'].includes(currency)
        || (value.interval !== 'month' && value.interval !== 'year')
        || !isNonNegativeInteger(regularAmount)
        || !isNonNegativeInteger(recurringAmount)
        || Number(recurringAmount) > Number(regularAmount)
        || !isNonNegativeInteger(value.amount_due_today_minor)
        || !validPercentOff
        || !validDiscountDuration
        || !isNonNegativeInteger(value.trial_period_days)
        || value.tax_behavior !== 'exclusive'
    ) {
        throw new CheckoutSessionError('Checkout returned invalid trial terms', {
            code: 'invalid_checkout_terms',
        });
    }

    return {
        version: 'preview_trial_v1',
        planId: value.plan_id,
        lookupKey: value.lookup_key,
        currency: currency as SupportedCurrency,
        interval: value.interval,
        regularRecurringAmountMinor: regularAmount,
        recurringAmountMinor: recurringAmount,
        amountDueTodayMinor: value.amount_due_today_minor,
        percentOff,
        discountDuration: discountDuration as 'continuous_subscription' | null,
        trialPeriodDays: value.trial_period_days,
        taxBehavior: 'exclusive',
    };
}

export class CheckoutSessionError extends Error {
    code: string | null;
    retryable: boolean;
    clearAttempt: boolean;

    constructor(message: string, options?: { code?: string; retryable?: boolean; clearAttempt?: boolean }) {
        super(message);
        this.name = 'CheckoutSessionError';
        this.code = options?.code ?? null;
        this.retryable = options?.retryable ?? false;
        this.clearAttempt = options?.clearAttempt ?? false;
    }
}

export function isCheckoutSessionError(error: unknown): error is CheckoutSessionError {
    return error instanceof CheckoutSessionError;
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => {
    setTimeout(resolve, ms);
});

async function checkoutErrorBody(error: unknown): Promise<CreateCheckoutSessionResponse | null> {
    const context = (error as { context?: unknown })?.context;

    if (context instanceof Response) {
        try {
            const body = await context.clone().json();
            if (body && typeof body === 'object') {
                return body as CreateCheckoutSessionResponse;
            }
        } catch {
            // Fall back to the Supabase function error message below.
        }
    }

    return null;
}

function checkoutErrorStatus(error: unknown): number | null {
    const context = (error as { context?: unknown })?.context;

    if (context instanceof Response) {
        return context.status;
    }

    return null;
}

function isRetryableFunctionTransportError(error: unknown): boolean {
    const errorName = (error as { name?: unknown })?.name;

    return typeof errorName === 'string' && RETRYABLE_FUNCTION_ERROR_NAMES.has(errorName);
}

async function checkoutErrorFromFunction(error: unknown): Promise<CheckoutSessionError> {
    const body = await checkoutErrorBody(error);
    const fallbackMessage = toError(error).message;
    const status = checkoutErrorStatus(error);
    const retryableStatus = typeof status === 'number' && status >= 500;

    return new CheckoutSessionError(
        typeof body?.error === 'string' && body.error.trim()
            ? body.error
            : fallbackMessage,
        {
            code: typeof body?.code === 'string' ? body.code : undefined,
            retryable: body?.retryable === true || retryableStatus || isRetryableFunctionTransportError(error),
            clearAttempt: body?.clear_attempt === true,
        },
    );
}

function shouldRetryCheckoutSession(error: CheckoutSessionError, checkoutAttemptId: string | undefined): boolean {
    return Boolean(checkoutAttemptId)
        && error.code !== ACCOUNT_OFFER_UNAVAILABLE_CODE
        && error.retryable
        && !error.clearAttempt;
}

export async function createCheckoutSession({
    lookupKey,
    currency,
    checkoutIntent,
    checkoutAttemptId,
    expectedDiscountGrantId,
    previewProjectId,
    resultDigestId,
    replaceCheckoutAttemptId,
    interactionMode,
}: CreateCheckoutSessionInput): Promise<{
    url: string;
    checkoutAttemptId: string | null;
    checkoutTerms: CheckoutTerms | null;
}> {
    const requestBody = {
        lookup_key: lookupKey,
        currency: currency.toLowerCase(),
        ...(checkoutIntent ? { checkout_intent: checkoutIntent } : {}),
        ...(checkoutAttemptId ? { checkout_attempt_id: checkoutAttemptId } : {}),
        ...(expectedDiscountGrantId
            ? { expected_discount_grant_id: expectedDiscountGrantId }
            : {}),
        ...(previewProjectId ? { preview_project_id: previewProjectId } : {}),
        ...(resultDigestId ? { result_digest_id: resultDigestId } : {}),
        ...(replaceCheckoutAttemptId
            ? { replace_checkout_attempt_id: replaceCheckoutAttemptId }
            : {}),
        ...(interactionMode ? { ui_mode: 'hosted' } : {}),
        ...(interactionMode ? { interaction_mode: interactionMode } : {}),
    };
    const maxAttempts = checkoutAttemptId ? CHECKOUT_SESSION_MAX_ATTEMPTS_WITH_ID : 1;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            const { data, error } = await supabase.functions.invoke<CreateCheckoutSessionResponse>(
                'create-checkout-session',
                {
                    method: 'POST',
                    body: requestBody,
                },
            );

            if (error) {
                throw await checkoutErrorFromFunction(error);
            }

            if (!data?.url) {
                throw new CheckoutSessionError(data?.error || 'Checkout session was not created', {
                    code: data?.code,
                    retryable: data?.retryable,
                    clearAttempt: data?.clear_attempt,
                });
            }

            return {
                url: data.url,
                checkoutAttemptId: data.checkout_attempt_id ?? null,
                checkoutTerms: parseCheckoutTerms(data.checkout_terms),
            };
        } catch (caughtError) {
            const checkoutError = isCheckoutSessionError(caughtError)
                ? caughtError
                : await checkoutErrorFromFunction(caughtError);

            if (attempt < maxAttempts && shouldRetryCheckoutSession(checkoutError, checkoutAttemptId)) {
                await wait(CHECKOUT_SESSION_RETRY_DELAY_MS);
                continue;
            }

            throw checkoutError;
        }
    }

    throw new CheckoutSessionError('Checkout session was not created');
}

export async function createEmbeddedCheckoutSession({
    lookupKey,
    currency,
    checkoutIntent,
    checkoutAttemptId,
    expectedDiscountGrantId,
    previewProjectId,
    resultDigestId,
    replaceCheckoutAttemptId,
    interactionMode,
}: CreateCheckoutSessionInput): Promise<EmbeddedCheckoutSession> {
    const requestBody = {
        lookup_key: lookupKey,
        currency: currency.toLowerCase(),
        ui_mode: 'embedded',
        ...(checkoutIntent ? { checkout_intent: checkoutIntent } : {}),
        ...(checkoutAttemptId ? { checkout_attempt_id: checkoutAttemptId } : {}),
        ...(expectedDiscountGrantId
            ? { expected_discount_grant_id: expectedDiscountGrantId }
            : {}),
        ...(previewProjectId ? { preview_project_id: previewProjectId } : {}),
        ...(resultDigestId ? { result_digest_id: resultDigestId } : {}),
        ...(replaceCheckoutAttemptId
            ? { replace_checkout_attempt_id: replaceCheckoutAttemptId }
            : {}),
        ...(interactionMode ? { interaction_mode: interactionMode } : {}),
    };
    const maxAttempts = checkoutAttemptId ? CHECKOUT_SESSION_MAX_ATTEMPTS_WITH_ID : 1;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            const { data, error } = await supabase.functions.invoke<CreateCheckoutSessionResponse>(
                'create-checkout-session',
                {
                    method: 'POST',
                    body: requestBody,
                },
            );

            if (error) {
                throw await checkoutErrorFromFunction(error);
            }

            if (!data?.client_secret) {
                throw new CheckoutSessionError(data?.error || 'Embedded checkout session was not created', {
                    code: data?.code,
                    retryable: data?.retryable,
                    clearAttempt: data?.clear_attempt,
                });
            }

            return {
                clientSecret: data.client_secret,
                publishableKey: data.publishable_key ?? null,
                checkoutAttemptId: data.checkout_attempt_id ?? null,
                checkoutTerms: parseCheckoutTerms(data.checkout_terms),
            };
        } catch (caughtError) {
            const checkoutError = isCheckoutSessionError(caughtError)
                ? caughtError
                : await checkoutErrorFromFunction(caughtError);

            if (attempt < maxAttempts && shouldRetryCheckoutSession(checkoutError, checkoutAttemptId)) {
                await wait(CHECKOUT_SESSION_RETRY_DELAY_MS);
                continue;
            }

            throw checkoutError;
        }
    }

    throw new CheckoutSessionError('Embedded checkout session was not created');
}

export async function prepareCheckoutSession({
    lookupKey,
    currency,
    checkoutIntent,
    checkoutAttemptId,
    expectedDiscountGrantId,
    previewProjectId,
    resultDigestId,
    replaceCheckoutAttemptId,
    uiMode = 'embedded',
}: Omit<CreateCheckoutSessionInput, 'interactionMode'> & {
    uiMode?: 'hosted' | 'embedded';
}): Promise<PreparedCheckoutSession> {
    const requestBody = {
        lookup_key: lookupKey,
        currency: currency.toLowerCase(),
        ui_mode: uiMode,
        interaction_mode: 'prepare',
        ...(checkoutIntent ? { checkout_intent: checkoutIntent } : {}),
        ...(checkoutAttemptId ? { checkout_attempt_id: checkoutAttemptId } : {}),
        ...(expectedDiscountGrantId
            ? { expected_discount_grant_id: expectedDiscountGrantId }
            : {}),
        ...(previewProjectId ? { preview_project_id: previewProjectId } : {}),
        ...(resultDigestId ? { result_digest_id: resultDigestId } : {}),
        ...(replaceCheckoutAttemptId
            ? { replace_checkout_attempt_id: replaceCheckoutAttemptId }
            : {}),
    };
    const maxAttempts = checkoutAttemptId ? CHECKOUT_SESSION_MAX_ATTEMPTS_WITH_ID : 1;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            const { data, error } = await supabase.functions.invoke<CreateCheckoutSessionResponse>(
                'create-checkout-session',
                {
                    method: 'POST',
                    body: requestBody,
                },
            );

            if (error) {
                throw await checkoutErrorFromFunction(error);
            }

            const parsedTerms = parseCheckoutTerms(data?.checkout_terms);
            let preparedAttemptId: string;
            try {
                preparedAttemptId = parsePreparedCheckoutAttemptId(
                    data?.checkout_attempt_id,
                );
            } catch {
                throw new CheckoutSessionError(
                    data?.error || 'Checkout terms were not prepared',
                    {
                        code: data?.code,
                        retryable: data?.retryable,
                        clearAttempt: data?.clear_attempt,
                    },
                );
            }
            if (!parsedTerms) {
                throw new CheckoutSessionError(data?.error || 'Checkout terms were not prepared', {
                    code: data?.code,
                    retryable: data?.retryable,
                    clearAttempt: data?.clear_attempt,
                });
            }

            return {
                // A passive prepare may intentionally return an exact prior
                // interacted attempt from another browser. The authenticated
                // server binding is authoritative; the local UUID is only a
                // draft idempotency key.
                checkoutAttemptId: preparedAttemptId,
                checkoutTerms: parsedTerms,
            };
        } catch (caughtError) {
            const checkoutError = isCheckoutSessionError(caughtError)
                ? caughtError
                : await checkoutErrorFromFunction(caughtError);

            if (attempt < maxAttempts && shouldRetryCheckoutSession(checkoutError, checkoutAttemptId)) {
                await wait(CHECKOUT_SESSION_RETRY_DELAY_MS);
                continue;
            }

            throw checkoutError;
        }
    }

    throw new CheckoutSessionError('Checkout terms were not prepared');
}
